import React, { useEffect, useRef, useState } from "react";
import Modal from "../../Components/Modal";
import Card from "../../Components/Card";
import { FaChevronDown } from "react-icons/fa";
import { useAppContext, emptyOrderState } from "../../context/AppContext";
import { opmStorage, Keys } from "../../util/opmStorage";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import Button from "../../Components/Button";
import Price from "../../Components/Price";
import ChefThanks from "./ChefThanks";

const Index = ({
  closeModal,
  t,
  setOrderList,
  setCurrentOrderList,
  setCheckoutOrder,
  navigate,
  orderList,
  getPrice,
  getTipValue,
}) => {
  const {
    guestRestaurantId,
    storageElementId,
    setDiningSessionId,
  } = useAppContext();
  const paidNow = useLocation().state;
  const [showChevron, setShowChevron] = useState(false);
  const orderListRef = useRef(null);

  useEffect(() => {
    const el = orderListRef.current;
    if (!el) return;

    const isScrollable = el.scrollHeight > el.clientHeight;
    setShowChevron(
      isScrollable && el.scrollTop + el.clientHeight < el.scrollHeight - 1
    );

    const handleScroll = () => {
      setShowChevron(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [orderList?.items]);

  return (
    <Modal
      onClose={closeModal}
      isCloseIcon={false}
      isBottomButton={false}
      isHeader={false}
      bottomComponent={
        <div className="flex flex-col gap-2 sticky bottom-0 z-10 bg-background shadow-sheet px-4 pb-5 pt-3">
          <Button
            size="lg"
            fullWidth
            onClick={() => {
              if (guestRestaurantId && storageElementId) {
                opmStorage.remove(
                  Keys.diningSessionId(guestRestaurantId, storageElementId)
                );
              }
              setDiningSessionId(null);
              setCheckoutOrder(null);
              setOrderList(emptyOrderState());
              setCurrentOrderList(emptyOrderState());
              navigate("/");
            }}
          >
            {t("View Receipt")}
          </Button>
          <Button variant="secondary" fullWidth>
            {t("Share Feedback")}
          </Button>
        </div>
      }
      isOverFlow={false}
    >
      <div className="flex flex-col gap-5 justify-center items-center w-full h-full px-4 py-6">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
          className="w-full flex justify-center"
        >
          <ChefThanks t={t} />
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col gap-1 justify-center items-center"
        >
          <h2 className="text-xl font-bold text-dark">
            {t("Payment Successful")}
          </h2>
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="w-full"
        >
          {paidNow ? (
            <Card className="p-4 flex flex-col gap-2.5 w-full">
              <h3 className="text-base font-bold text-dark">{t("Payment summary")}</h3>
              <div className="flex justify-between text-sm">
                <span className="text-muted">{t("Your share")}</span>
                <Price className="text-dark" value={paidNow.base} />
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted">{t("Tip")}</span>
                <Price className="text-dark" value={paidNow.tip} />
              </div>
              <div className="flex justify-between items-center pt-2.5 border-t border-border text-base font-bold text-dark">
                <span>{t("Charged")}</span>
                <Price value={paidNow.charge} />
              </div>
              {paidNow.walletName && (
                <span className="text-xs text-muted">{t("paidWith", { method: paidNow.walletName })}</span>
              )}
            </Card>
          ) : (
          <Card
            className="p-4 flex flex-col gap-3 w-full relative"
            key={t("Order Summary")}
          >
            <h3 className="text-base font-bold text-dark">
              {t("Order Summary")}
            </h3>
            <div
              ref={orderListRef}
              className="flex flex-col gap-2 overflow-auto h-full max-h-32 custom-scrollbar"
            >
              {orderList?.items?.map((item) => (
                <div
                  className="flex flex-row justify-between text-sm"
                  key={item?.id}
                >
                  <span className="text-muted truncate min-w-0">
                    {item?.translations?.name}
                  </span>
                  <Price className="text-dark" value={item?.price} />
                </div>
              ))}
            </div>

            {showChevron && (
              <div className="absolute start-1/2 bottom-24 animate-bounce text-muted">
                <FaChevronDown size={12} />
              </div>
            )}

            <div className="w-full flex flex-row justify-between items-center pt-3 border-t border-border text-sm">
              <span className="text-muted">{t("Tip")}</span>
              <Price
                className="text-dark"
                value={getTipValue(
                  orderList?.order_summary?.actual_amount,
                  orderList?.order_summary?.tip
                )}
              />
            </div>
            <div className="w-full flex flex-row justify-between items-center">
              <span className="text-base font-bold text-dark">
                {t("Total")}
              </span>
              <Price
                className="text-base font-bold text-dark"
                value={orderList?.order_summary?.total_amount}
              />
            </div>
          </Card>
          )}
        </motion.div>
      </div>
    </Modal>
  );
};

export default Index;
